import { useEffect, useState } from "react";
import { fadeNotification, readyNotification, removeNotification } from "../redux/actions/NotificationActions";
import type { NotificationType } from "../types/CommonTypes";
import type { CommonDispatchType } from "../redux/commonStore";
import { useCommonSelector } from "../redux/commonStore";
import { useDispatch } from "../../shared-logic/redux/store";

type useNotificationContainerType = {
    notifications: NotificationType[];
    isFadingOut: boolean; // No longer needed for Apheleia but still here to support the old notification flow in the old build.
    onTransitionEnd: (index: number) => void; // This onTransitionEnd must be added on the wrapper which starts the fadeout animation based on the property isFadingOut
};

type useNotificationContainerOptionType = {
    useCustomTimeout: boolean; // Property to not have the timeout built in the hook. Used for Apheleia.
};
const useNotificationContainer = (options?: useNotificationContainerOptionType): useNotificationContainerType => {
    const notifications = useCommonSelector((state) => state.notification?.notifications) ?? [];
    const dispatch = useDispatch<CommonDispatchType>();

    const [isFadingOut, setIsFadingOut] = useState(false); // No longer needed for Apheleia but still here to support the old notification flow in the old build.

    // This will trigger the fade out animation
    // When the animation is done the 'onTransitionEnd' will be called which will clean up the notification
    const clearNotification = (): void => fade();

    const fade = (): void => {
        setIsFadingOut(true);
        const notificationToFade = notifications[notifications?.length - 1];

        const notificationsToFade = getNotificationsToModify(notificationToFade);
        if (!notificationToFade.displaySettings?.isAnimating) dispatch(fadeNotification(notificationsToFade));
    };

    // As multiple onTransitionEnd triggers are used, the isReady state is used to know whether the inital animation is ready.
    // As soon as the initial animation is ready, the notification needs to be removed when onTransitionEnd gets called.
    const onTransitionEnd = (index: number): void => {
        const notification = notifications.find((notify) => notify.displaySettings?.notificationIndex === index);
        if (notification) {
            if (notification.displaySettings?.isAnimating || notification.displaySettings?.isReady) {
                setIsFadingOut(false);
                dispatch(removeNotification(notification));
            } else {
                const notificationsToFade = getNotificationsToModify(notification);
                dispatch(readyNotification(notificationsToFade));
            }
        }
    };

    // Notifications can be linked, e.g. if a colour & upholstery notification was added at the same time, they also need to fade away simultaneously.
    const getNotificationsToModify = (notificationToModify: NotificationType): NotificationType[] => {
        const notificationsToModify = notifications.filter(
            (notification) =>
                notification.displaySettings?.linkedNotificationIndex ===
                notificationToModify.displaySettings?.linkedNotificationIndex,
        );

        return notificationsToModify;
    };

    useEffect(() => {
        if (notifications.length && !options?.useCustomTimeout) setTimeout(clearNotification, 5000);
    }, [notifications]);

    return {
        notifications,
        isFadingOut,
        onTransitionEnd,
    };
};

export default useNotificationContainer;
